import { Injectable } from '@angular/core';
import * as ExcelJS from 'exceljs';
import { type Quote } from '../../../models/quote.model';
import { CREDIT_CARD_SALE_NAME } from '../quote-helpers';

// Table statuses the quote log hides the hours-remaining indicator for.
const HOURS_HIDDEN_STATUSES = ['Closed Won', 'Closed Lost', 'Submitted'];

const HEADERS = [
	'Received',
	'Due',
	'Hours',
	'Customer',
	'Email Summary',
	'Outside Rep',
	'Inside Rep',
	'Estimate #',
	'NS Status',
	'Quote Status',
	'Errors'
];

const HEADER_FILL: ExcelJS.Fill = {
	type: 'pattern',
	pattern: 'solid',
	fgColor: { argb: 'FFD0D0D0' }
};

const COLUMN_WIDTH_PADDING = 2;

@Injectable({ providedIn: 'root' })
export class QuoteLogExportService {
	// Mirrors the quote log table's own display logic column-for-column so the
	// export matches what the user sees on screen, minus attachments and the
	// raw email payload — neither of which the Quote view model even carries.
	public async exportQuoteLog(
		quotes: Quote[],
		start: Date,
		end: Date
	): Promise<void> {
		const startOfDay = new Date(start);
		startOfDay.setHours(0, 0, 0, 0);
		const endOfDay = new Date(end);
		endOfDay.setHours(23, 59, 59, 999);

		const rows = quotes.filter((q) => {
			const received = new Date(q.receivedDate);
			return received >= startOfDay && received <= endOfDay;
		});

		const workbook = new ExcelJS.Workbook();
		const worksheet = workbook.addWorksheet('Quote Log');

		const headerRow = worksheet.addRow(HEADERS);
		headerRow.eachCell((cell) => {
			cell.fill = HEADER_FILL;
			cell.font = { bold: true, size: 12 };
		});
		HEADERS.forEach((header, index) => {
			worksheet.getColumn(index + 1).width =
				(header.length + COLUMN_WIDTH_PADDING) * 1.3;
		});

		rows.forEach((q) => worksheet.addRow(this.toExportRow(q)));

		const buffer = await workbook.xlsx.writeBuffer();
		QuoteLogExportService.downloadBuffer(
			buffer,
			`RK Supply Quote-Log_${QuoteLogExportService.dateStamp(start)}_${QuoteLogExportService.dateStamp(end)}.xlsx`
		);
	}

	private static dateStamp(date: Date): string {
		const mm = String(date.getMonth() + 1).padStart(2, '0');
		const dd = String(date.getDate()).padStart(2, '0');
		return `${mm}${dd}${date.getFullYear()}`;
	}

	private toExportRow(quote: Quote): string[] {
		return [
			this.excelSafe(quote.receivedLabel),
			this.excelSafe(
				quote.dueLabel + (quote.dueDateDefaulted ? ' (defaulted)' : '')
			),
			this.excelSafe(this.hoursExportLabel(quote)),
			this.excelSafe(quote.nsLocationName ?? quote.nsCustomerName ?? ''),
			this.excelSafe(quote.emailSummary),
			this.excelSafe(quote.outsideRep.name),
			this.excelSafe(quote.insideRep ?? '—'),
			this.excelSafe(quote.estimateNumber ?? '—'),
			this.excelSafe(quote.nsEstimateStatus ?? '—'),
			this.excelSafe(quote.nsQuoteStatus ?? '—'),
			this.customerIsDefaultOrEmpty(quote)
				? 'No NetSuite customer match'
				: 'OK'
		];
	}

	private hoursExportLabel(quote: Quote): string {
		if (HOURS_HIDDEN_STATUSES.includes(quote.nsEstimateStatus ?? '')) {
			return '';
		}
		if (quote.hoursRemaining === null) return '—';
		if (quote.isOverdue) return `${Math.abs(quote.hoursRemaining)}h OVERDUE`;
		return `${quote.hoursRemaining}h`;
	}

	private customerIsDefaultOrEmpty(quote: Quote): boolean {
		if (quote.nsCustomerName == null || quote.nsCustomerName === '') {
			return true;
		}
		return quote.nsCustomerName === CREDIT_CARD_SALE_NAME;
	}

	// Excel treats a leading =, +, - or @ as a formula, and scraped descriptions can
	// legitimately start with any of them. Prefixing with an apostrophe keeps the cell text.
	private excelSafe(value: string | null): string {
		if (!value) return '';
		return /^[=+\-@]/.test(value) ? `'${value}` : value;
	}

	private static downloadBuffer(buffer: ExcelJS.Buffer, fileName: string): void {
		const blob = new Blob([buffer], {
			type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
		});
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = fileName;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	}
}
